import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { toast } from '@/stores/useToastStore';
import { useAuthStore } from '@/stores/useAuthStore';
import { ApiError, ERROR_MESSAGES } from './errors';
import { CustomerAuthResponse } from '@/types';
import { setSessionId } from '@/hooks/useSession';

/**
 * Configured Axios instance for API calls
 *
 * Features:
 * - Automatic auth token attachment
 * - Centralized error handling
 * - User-friendly error notifications
 * - Timeout configuration
 */
const api = axios.create({
    baseURL: import.meta.env.VITE_API_URL || '/api',
    headers: {
        'Content-Type': 'application/json',
    },
    timeout: 30000, // 30 second timeout
});

// Request interceptor - attach auth token + Accept-Language so the API can pick the right
// email template language for any send triggered as a side effect of this request.
api.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
        // Phase 3: Zustand auth store is the single source of truth for tokens.
        // `useAuthStore.getState()` is safe to call during RR v7 prerender (Node
        // SSR) too — the store initializes to `{ accessToken: null, ... }` and
        // the persist middleware no-ops when localStorage is undefined.
        const token = useAuthStore.getState().accessToken;
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }

        // Browsers expose navigator.language (e.g. "es-ES"). The backend's RequestLanguageProvider
        // takes the highest-quality tag, lowercases it, and trims to the primary subtag.
        const browserLang = typeof navigator !== 'undefined' ? navigator.language : undefined;
        if (browserLang && !config.headers['Accept-Language']) {
            config.headers['Accept-Language'] = browserLang;
        }

        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

/**
 * Create a structured ApiError from an Axios error
 */
function createApiError(error: AxiosError): ApiError {
    // Handle network errors (no response)
    if (!error.response) {
        // Check for timeout
        if (error.code === 'ECONNABORTED') {
            return {
                status: 0,
                message: ERROR_MESSAGES.TIMEOUT_ERROR,
                originalMessage: error.message,
                isNetworkError: true,
                shouldRedirect: false,
            };
        }
        // General network error
        return {
            status: 0,
            message: ERROR_MESSAGES.NETWORK_ERROR,
            originalMessage: error.message,
            isNetworkError: true,
            shouldRedirect: false,
        };
    }

    const status = error.response.status;
    const data = error.response.data as Record<string, unknown>;
    const serverMessage = typeof data?.message === 'string' ? data.message : undefined;

    // Handle specific status codes
    switch (status) {
        case 401:
            return {
                status,
                message: ERROR_MESSAGES.SESSION_EXPIRED,
                originalMessage: serverMessage,
                isNetworkError: false,
                shouldRedirect: true,
                redirectTo: '/login',
            };

        case 403:
            return {
                status,
                message: ERROR_MESSAGES.ACCESS_DENIED,
                originalMessage: serverMessage,
                isNetworkError: false,
                shouldRedirect: false,
            };

        case 404:
            // Don't show toast for 404s - let components handle them
            return {
                status,
                message: ERROR_MESSAGES.NOT_FOUND,
                originalMessage: serverMessage,
                isNetworkError: false,
                shouldRedirect: false,
            };

        case 400:
        case 422: {
            // Validation errors - extract field errors if present
            const fieldErrors = (data?.errors as Record<string, string[]>) || undefined;
            return {
                status,
                message: serverMessage || ERROR_MESSAGES.VALIDATION_ERROR,
                originalMessage: serverMessage,
                fieldErrors,
                isNetworkError: false,
                shouldRedirect: false,
            };
        }

        default:
            // 5xx and other errors
            if (status >= 500) {
                return {
                    status,
                    message: ERROR_MESSAGES.SERVER_ERROR,
                    originalMessage: serverMessage,
                    isNetworkError: false,
                    shouldRedirect: false,
                };
            }
            return {
                status,
                message: serverMessage || ERROR_MESSAGES.UNKNOWN_ERROR,
                originalMessage: serverMessage,
                isNetworkError: false,
                shouldRedirect: false,
            };
    }
}

/**
 * Handle the error: show toast, clear auth, etc.
 */
function handleApiError(apiError: ApiError, skipToast: boolean = false): void {
    // Show toast for most errors (except 404s which components should handle).
    // 401s get special handling — they're prone to spamming the user, and
    // anonymous visitors shouldn't see a "session expired" notice at all.
    if (apiError.status === 401) {
        maybeShowSessionExpiredToast();
        return;
    }
    if (apiError.status !== 404 && !skipToast) {
        toast.error(apiError.message);
    }
}

/**
 * Show the "your session has expired" notice at most once per ~5 minutes,
 * and never to a visitor who never had a session in the first place.
 *
 * Two cases trigger 401-with-no-refresh-token in this codebase:
 *   1. Anonymous user — page made an authed call (e.g. fetching a saved cart),
 *      backend rejects. There's no session to expire; toasting is misleading
 *      and clutters the UI. We skip the toast entirely.
 *   2. Previously-authed user whose tokens have been cleared (logout, manual
 *      cookie clear, etc.). The toast is informative — but only the first
 *      time. Multiple parallel queries that all 401 should not each fire
 *      their own toast.
 */
let lastSessionExpiredToastAt = 0;
const SESSION_EXPIRED_TOAST_COOLDOWN_MS = 5 * 60 * 1000;
function maybeShowSessionExpiredToast(): void {
    if (typeof window === 'undefined') return;
    // Phase 3: read auth state from the Zustand store, not raw localStorage.
    const { accessToken, refreshToken } = useAuthStore.getState();
    if (!accessToken && !refreshToken) {
        // Truly anonymous — nothing expired
        return;
    }
    const now = Date.now();
    if (now - lastSessionExpiredToastAt < SESSION_EXPIRED_TOAST_COOLDOWN_MS) {
        return;
    }
    lastSessionExpiredToastAt = now;
    toast.error(ERROR_MESSAGES.SESSION_EXPIRED);
}

// Track if we're currently refreshing to prevent multiple simultaneous refreshes
let isRefreshing = false;
let refreshSubscribers: Array<(token: string) => void> = [];

/**
 * Subscribe to token refresh completion
 */
function subscribeTokenRefresh(callback: (token: string) => void) {
    refreshSubscribers.push(callback);
}

/**
 * Notify all subscribers when refresh completes
 */
function onTokenRefreshed(token: string) {
    refreshSubscribers.forEach(callback => callback(token));
    refreshSubscribers = [];
}

// Response interceptor - handle errors, token refresh, and session ID storage
api.interceptors.response.use(
    (response) => {
        // Check for backend-generated session ID (for anonymous users on first request)
        const generatedSessionId = response.headers['x-generated-session-id'];
        if (generatedSessionId) {
            setSessionId(generatedSessionId);
        }
        return response;
    },
    async (error: AxiosError) => {
        const originalRequest = error.config as
            | (InternalAxiosRequestConfig & { _retry?: boolean })
            | undefined;

        // Some low-level failures (network, abort) surface without a `.config`.
        // Skip the auth/refresh path in that case — there's nothing to retry.
        if (!originalRequest) {
            const apiError = createApiError(error);
            handleApiError(apiError);
            return Promise.reject(apiError);
        }

        // Skip token refresh for auth endpoints (login, register, refresh)
        const isAuthEndpoint = originalRequest.url?.includes('/auth/login') ||
                               originalRequest.url?.includes('/auth/register') ||
                               originalRequest.url?.includes('/auth/refresh');

        // Handle 401 Unauthorized - attempt token refresh (but not for auth endpoints)
        if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
            // Phase 3: read from the Zustand auth store (single source of truth).
            const refreshToken = useAuthStore.getState().refreshToken;

            if (!refreshToken) {
                const apiError = createApiError(error);
                handleApiError(apiError);
                return Promise.reject(apiError);
            }

            if (isRefreshing) {
                // If already refreshing, queue this request
                return new Promise((resolve) => {
                    subscribeTokenRefresh((token: string) => {
                        originalRequest.headers.Authorization = `Bearer ${token}`;
                        resolve(api(originalRequest));
                    });
                });
            }

            originalRequest._retry = true;
            isRefreshing = true;

            try {
                const response = await axios.post<CustomerAuthResponse>(
                    `${api.defaults.baseURL}/customer/auth/refresh`,
                    { refreshToken },
                    {
                        headers: { 'Content-Type': 'application/json' },
                    }
                );

                const { accessToken, refreshToken: newRefreshToken, expiresAt, customer } = response.data;

                // Update axios header for this request
                originalRequest.headers.Authorization = `Bearer ${accessToken}`;

                // Phase 3: `setAuth()` updates the Zustand store; the persist
                // middleware writes the full state (including tokens) to
                // `stork-auth-storage`. No raw-key dual-write.
                useAuthStore.getState().setAuth(customer, accessToken, newRefreshToken, expiresAt);

                // Notify all queued requests
                onTokenRefreshed(accessToken);

                // Retry the original request
                return api(originalRequest);
            } catch (refreshError) {
                console.error('[Axios] Token refresh failed:', refreshError);

                // Phase 3: `logout()` clears the store state; the persist
                // middleware writes the cleared blob back to `stork-auth-storage`.
                // This also flips `isAuthenticated` so any mounted ProtectedRoute
                // re-renders and any selector subscribers react accordingly.
                try {
                    useAuthStore.getState().logout();
                } catch {
                    // No-op — store may not be initialised in tests
                }

                // Customer-site policy: public routes (catalogue, product, locations,
                // cart, checkout, about, faq, etc.) MUST remain navigable for anonymous
                // users. Only redirect to /login when the user is actively on an
                // auth-required dashboard surface. ProtectedRoute will handle the
                // redirect naturally for those routes via its own <Navigate>, so this
                // is a safety net for pages outside that wrapper.
                const isOnAuthedSurface = /^\/[^/]+\/(account|profile|reservations|monthly-rentals)/.test(
                    window.location.pathname,
                );

                if (isOnAuthedSurface && !window.location.pathname.endsWith('/login')) {
                    // Throttled — see maybeShowSessionExpiredToast(). Multiple
                    // 401s on the dashboard surface used to fire one toast each.
                    maybeShowSessionExpiredToast();
                    setTimeout(() => {
                        const returnTo = window.location.pathname + window.location.search;
                        const localeMatch = window.location.pathname.match(/^\/([a-z]{2}(?:-[a-z]{2})?)\//i);
                        const locale = localeMatch?.[1] ?? 'en';
                        window.location.href = `/${locale}/login?from=${encodeURIComponent(returnTo)}`;
                    }, 100);
                }
                // Anonymous users + non-authed surfaces: silently reject. The calling
                // hook decides what to do (most return the rejected promise; React Query
                // surfaces it as an `error` state which the component can render or ignore).

                return Promise.reject(refreshError);
            } finally {
                isRefreshing = false;
            }
        }

        // For other errors, use existing error handling
        const apiError = createApiError(error);
        handleApiError(apiError);
        return Promise.reject(apiError);
    }
);

export default api;

// Re-export types for convenience
export type { ApiError };
